import React from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useAdminGAAnalytics } from './useAdminGAAnalytics';
import type { GASubTab } from './gaAnalyticsTypes';
import './AdminGAAnalyticsPage.css';

const COLORS = [
  '#06b6d4', '#8b5cf6', '#10b981', '#f59e0b',
  '#ef4444', '#3b82f6', '#ec4899', '#14b8a6',
  '#a855f7', '#f97316',
];

const SUB_TABS: { id: GASubTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'acquisition', label: 'Acquisition' },
  { id: 'funnel', label: 'User Funnel' },
  { id: 'pages', label: 'Pages & Engagement' },
  { id: 'errors', label: 'Drop-off & Errors' },
  { id: 'demographics', label: 'Demographics' },
];

const TIME_RANGES = [
  { value: 0, label: 'Today' },
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

function formatDate(dateStr: string): string {
  if (!dateStr || dateStr.length !== 8) return dateStr;
  const y = dateStr.slice(0, 4);
  const m = dateStr.slice(4, 6);
  const d = dateStr.slice(6, 8);
  const dt = new Date(`${y}-${m}-${d}T00:00:00`);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDuration(seconds: number): string {
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.round(seconds)}s`;
}

function formatPercent(val: number): string {
  return `${(val * 100).toFixed(1)}%`;
}

function formatNumber(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
  return String(Math.round(val));
}

// Reusable section error banner
const SectionError: React.FC<{ message: string }> = ({ message }) => (
  <div className="ga-section-error">
    <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
    {message}
  </div>
);

const EmptyState: React.FC<{ text?: string }> = ({ text = 'No data available for this time range.' }) => (
  <div className="ga-empty-state">{text}</div>
);

// Custom tooltip matching existing analytics style
const CustomTooltip: React.FC<any> = ({ active, payload, label, labelFormatter, valueFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="ga-tooltip">
      <p className="ga-tooltip-label">{labelFormatter ? labelFormatter(label) : label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="ga-tooltip-value" style={{ color: entry.color }}>
          {entry.name}: {valueFormatter ? valueFormatter(entry.value) : entry.value}
        </p>
      ))}
    </div>
  );
};

// Donut label
const DonutLabel: React.FC<any> = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = (innerRadius + outerRadius) / 2 + (outerRadius - innerRadius) * 0.35;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if ((percent ?? 0) < 0.04) return null;
  return (
    <text
      x={x} y={y} fill="#fff" stroke="#0f172a" strokeWidth={3} strokeLinejoin="round"
      paintOrder="stroke fill" textAnchor="middle" dominantBaseline="central"
      style={{ fontSize: '11px', fontWeight: 600 }}
    >
      {name}: {value} ({((percent ?? 0) * 100).toFixed(0)}%)
    </text>
  );
};


const AdminGAAnalyticsPage: React.FC = () => {
  const {
    data, loading, sectionErrors, timeRange, setTimeRange,
    activeSubTab, setActiveSubTab, lastRefreshed, refresh,
  } = useAdminGAAnalytics();

  // --- Loading state ---
  if (loading && !data.overview) {
    return (
      <div className="ga-page">
        <div className="ga-loading">
          <div className="ga-loading-spinner" />
          <p>Loading site analytics...</p>
        </div>
      </div>
    );
  }

  const kpis = data.overview?.kpis;

  return (
    <div className="ga-page">
      {/* Header bar */}
      <div className="ga-header">
        <div className="ga-header-left">
          <h2 className="ga-title">Site Analytics</h2>
          {lastRefreshed && (
            <span className="ga-last-refreshed">
              Last refreshed: {lastRefreshed.toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="ga-header-right">
          <button className="ga-refresh-btn" onClick={refresh} disabled={loading} title="Clear cache & refresh">
            <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
              <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <select
            className="ga-time-select"
            value={timeRange}
            onChange={(e) => setTimeRange(Number(e.target.value))}
          >
            {TIME_RANGES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Sub-tab navigation */}
      <nav className="ga-subtabs">
        {SUB_TABS.map(({ id, label }) => (
          <button
            key={id}
            className={`ga-subtab ${activeSubTab === id ? 'ga-subtab-active' : ''}`}
            onClick={() => setActiveSubTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {/* ===== OVERVIEW ===== */}
      {activeSubTab === 'overview' && (
        <div className="ga-section">
          {sectionErrors.overview ? (
            <SectionError message={sectionErrors.overview} />
          ) : kpis ? (
            <>
              <div className="ga-kpi-grid">
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">Active Users</span>
                  <span className="ga-kpi-value">{formatNumber(kpis.activeUsers)}</span>
                </div>
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">New Users</span>
                  <span className="ga-kpi-value">{formatNumber(kpis.newUsers)}</span>
                </div>
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">Sessions</span>
                  <span className="ga-kpi-value">{formatNumber(kpis.sessions)}</span>
                </div>
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">Engagement Rate</span>
                  <span className="ga-kpi-value">{formatPercent(kpis.engagementRate)}</span>
                </div>
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">Avg Session Duration</span>
                  <span className="ga-kpi-value">{formatDuration(kpis.averageSessionDuration)}</span>
                </div>
                <div className="ga-kpi-card">
                  <span className="ga-kpi-label">Pages / Session</span>
                  <span className="ga-kpi-value">{kpis.screenPageViewsPerSession.toFixed(1)}</span>
                </div>
              </div>

              {/* Trends mini-chart */}
              {data.trends?.daily && data.trends.daily.length > 0 && (
                <div className="ga-chart-card">
                  <h3 className="ga-chart-title">User Trends</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={data.trends.daily}>
                      <defs>
                        <linearGradient id="gaAreaUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gaAreaSessions" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip labelFormatter={formatDate} />} />
                      <Legend />
                      <Area type="monotone" dataKey="activeUsers" name="Active Users" stroke="#06b6d4" fill="url(#gaAreaUsers)" />
                      <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#8b5cf6" fill="url(#gaAreaSessions)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top events table */}
              {data.overview?.top_events && data.overview.top_events.length > 0 && (
                <div className="ga-chart-card">
                  <h3 className="ga-chart-title">Top Custom Events</h3>
                  <div className="ga-table-wrap">
                    <table className="ga-table">
                      <thead>
                        <tr><th>Event</th><th>Count</th><th>Users</th></tr>
                      </thead>
                      <tbody>
                        {data.overview.top_events.map((evt) => (
                          <tr key={evt.eventName}>
                            <td className="ga-event-name">{evt.eventName}</td>
                            <td>{formatNumber(evt.eventCount)}</td>
                            <td>{formatNumber(evt.totalUsers)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {/* ===== ACQUISITION ===== */}
      {activeSubTab === 'acquisition' && (
        <div className="ga-section">
          {sectionErrors.traffic ? (
            <SectionError message={sectionErrors.traffic} />
          ) : data.traffic ? (
            <div className="ga-two-col">
              {/* Channel pie */}
              <div className="ga-chart-card">
                <h3 className="ga-chart-title">Traffic Channels</h3>
                {data.traffic.channels.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={data.traffic.channels}
                        dataKey="sessions"
                        nameKey="sessionDefaultChannelGroup"
                        cx="50%" cy="50%"
                        outerRadius={120}
                        label={DonutLabel}
                        labelLine={false}
                      >
                        {data.traffic.channels.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState />
                )}
              </div>

              {/* Source bar */}
              <div className="ga-chart-card">
                <h3 className="ga-chart-title">Top Sources</h3>
                {data.traffic.sources.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={data.traffic.sources.slice(0, 10)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <YAxis dataKey="sessionSource" type="category" width={120} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="sessions" name="Sessions" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState />
                )}
              </div>
            </div>
          ) : (
            <EmptyState />
          )}
        </div>
      )}

      {/* ===== FUNNEL ===== */}
      {activeSubTab === 'funnel' && (
        <div className="ga-section">
          {sectionErrors.funnel ? (
            <SectionError message={sectionErrors.funnel} />
          ) : data.funnel?.steps && data.funnel.steps.length > 0 ? (
            <div className="ga-chart-card">
              <h3 className="ga-chart-title">Conversion Funnel</h3>
              <div className="ga-funnel">
                {data.funnel.steps.map((step, i) => {
                  const maxCount = data.funnel!.steps[0].count || 1;
                  const widthPct = Math.max((step.count / maxCount) * 100, 8);
                  return (
                    <div key={step.step} className="ga-funnel-step">
                      <div className="ga-funnel-label">
                        <span className="ga-funnel-step-num">{i + 1}</span>
                        <span className="ga-funnel-step-name">{step.label}</span>
                      </div>
                      <div className="ga-funnel-bar-wrap">
                        <div
                          className="ga-funnel-bar"
                          style={{
                            width: `${widthPct}%`,
                            backgroundColor: COLORS[i % COLORS.length],
                          }}
                        >
                          <span className="ga-funnel-count">{formatNumber(step.count)}</span>
                        </div>
                      </div>
                      {i > 0 && (
                        <span className="ga-funnel-dropoff">
                          {step.dropoff_pct > 0 ? `−${step.dropoff_pct}%` : '0%'} drop-off
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <EmptyState text="No funnel data yet. Events will appear once users interact with the app." />
          )}
        </div>
      )}

      {/* ===== PAGES & ENGAGEMENT ===== */}
      {activeSubTab === 'pages' && (
        <div className="ga-section">
          {sectionErrors.pages ? (
            <SectionError message={sectionErrors.pages} />
          ) : data.pages?.pages && data.pages.pages.length > 0 ? (
            <div className="ga-chart-card">
              <h3 className="ga-chart-title">Top Pages</h3>
              <div className="ga-table-wrap">
                <table className="ga-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Views</th>
                      <th>Avg Duration</th>
                      <th>Bounce Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pages.pages.map((p) => (
                      <tr key={p.pagePath}>
                        <td className="ga-page-path" title={p.pageTitle}>{p.pagePath}</td>
                        <td>{formatNumber(p.screenPageViews)}</td>
                        <td>{formatDuration(p.averageSessionDuration)}</td>
                        <td>{formatPercent(p.bounceRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <EmptyState />
          )}

          {/* Feature engagement */}
          {sectionErrors.engagement ? (
            <SectionError message={sectionErrors.engagement} />
          ) : data.engagement?.features && data.engagement.features.length > 0 ? (
            <div className="ga-chart-card" style={{ marginTop: '1.5rem' }}>
              <h3 className="ga-chart-title">Feature Adoption</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.engagement.features}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="group" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="event_count" name="Events" radius={[4, 4, 0, 0]}>
                    {data.engagement.features.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>
      )}

      {/* ===== DROP-OFF & ERRORS ===== */}
      {activeSubTab === 'errors' && (
        <div className="ga-section">
          {/* High bounce pages */}
          {sectionErrors.pages ? (
            <SectionError message={sectionErrors.pages} />
          ) : data.pages?.pages ? (
            <div className="ga-chart-card">
              <h3 className="ga-chart-title">High Bounce Pages</h3>
              {(() => {
                const highBounce = [...data.pages.pages]
                  .filter((p) => p.bounceRate > 0)
                  .sort((a, b) => b.bounceRate - a.bounceRate)
                  .slice(0, 10);
                return highBounce.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={highBounce} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tickFormatter={(v) => formatPercent(v)} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <YAxis dataKey="pagePath" type="category" width={160} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip valueFormatter={(v: number) => formatPercent(v)} />} />
                      <Bar dataKey="bounceRate" name="Bounce Rate" fill="#ef4444" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState text="No bounce data available." />
                );
              })()}
            </div>
          ) : null}

          {/* Error events */}
          {sectionErrors.errors ? (
            <SectionError message={sectionErrors.errors} />
          ) : data.errors?.errors && data.errors.errors.length > 0 ? (
            <div className="ga-chart-card" style={{ marginTop: '1.5rem' }}>
              <h3 className="ga-chart-title">Error & Friction Events</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.errors.errors}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="eventName" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="eventCount" name="Count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState text="No error events recorded in this period." />
          )}
        </div>
      )}

      {/* ===== DEMOGRAPHICS ===== */}
      {activeSubTab === 'demographics' && (
        <div className="ga-section">
          {sectionErrors.demographics ? (
            <SectionError message={sectionErrors.demographics} />
          ) : data.demographics ? (
            <>
              <div className="ga-two-col">
                {/* Devices */}
                <div className="ga-chart-card">
                  <h3 className="ga-chart-title">Devices</h3>
                  {data.demographics.devices.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={data.demographics.devices}
                          dataKey="totalUsers"
                          nameKey="deviceCategory"
                          cx="50%" cy="50%"
                          innerRadius={50} outerRadius={100}
                          label={DonutLabel}
                          labelLine={false}
                        >
                          {data.demographics.devices.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyState />
                  )}
                </div>

                {/* New vs Returning */}
                <div className="ga-chart-card">
                  <h3 className="ga-chart-title">New vs Returning</h3>
                  {data.demographics.new_vs_returning.length > 0 ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={data.demographics.new_vs_returning}
                          dataKey="totalUsers"
                          nameKey="newVsReturning"
                          cx="50%" cy="50%"
                          innerRadius={50} outerRadius={100}
                          label={DonutLabel}
                          labelLine={false}
                        >
                          {data.demographics.new_vs_returning.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyState />
                  )}
                </div>
              </div>

              {/* Countries */}
              {data.demographics.countries.length > 0 && (
                <div className="ga-chart-card" style={{ marginTop: '1.5rem' }}>
                  <h3 className="ga-chart-title">Top Countries</h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, data.demographics.countries.slice(0, 15).length * 32)}>
                    <BarChart data={data.demographics.countries.slice(0, 15)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <YAxis dataKey="country" type="category" width={140} tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="totalUsers" name="Users" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Browsers */}
              {data.demographics.browsers.length > 0 && (
                <div className="ga-chart-card" style={{ marginTop: '1.5rem' }}>
                  <h3 className="ga-chart-title">Browsers</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={data.demographics.browsers}
                        dataKey="totalUsers"
                        nameKey="browser"
                        cx="50%" cy="50%"
                        outerRadius={100}
                        label={DonutLabel}
                        labelLine={false}
                      >
                        {data.demographics.browsers.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          ) : (
            <EmptyState />
          )}
        </div>
      )}
    </div>
  );
};

export default AdminGAAnalyticsPage;
